import { Component, inject, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MatDivider } from '@angular/material/divider'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { FlattenedTemplateItem, TemplateItem, TemplateItemWithExpandedState } from '../types/template.types'

@Component({
  selector: 'app-template-preview-panel',
  standalone: true,
  imports: [CommonModule, MatDivider, TranslatePipe, TranslateDirective],
  templateUrl: './template-preview-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './template-preview-panel.component.scss',
})
export class TemplatePreviewPanelComponent {
  private translateService = inject(TranslateService)

  @Input({ required: true })
  set templateItems(items: TemplateItem[]) {
    const itemsWithExpandedState = items.map((item) => this.addExpandedStateToItem(item))
    this._templateItems = itemsWithExpandedState
    this._flattenedItems = this.flattenItems(this._templateItems)
    this._totalDuration = this.calculateTotalDuration(this._flattenedItems)
  }

  @Output()
  itemClicked = new EventEmitter<number>()

  private _templateItems: TemplateItemWithExpandedState[] = []
  private _flattenedItems: FlattenedTemplateItem[] = []
  private _totalDuration: number = 0

  get flattenedItems(): FlattenedTemplateItem[] {
    return this._flattenedItems
  }

  get totalDuration(): number {
    return this._totalDuration
  }

  getItemText(item: FlattenedTemplateItem): string {
    const minutesPart = item.duration !== null ? ` (${item.duration} ${this.translateService.instant('TEMPLATES.EDITOR.MINUTES_SUFFIX')})` : ''
    return `${item.itemNumber}. ${item.title}${minutesPart}`
  }

  onItemClick(itemId: number): void {
    this.itemClicked.emit(itemId)
  }

  private addExpandedStateToItem(item: TemplateItem): TemplateItemWithExpandedState {
    return {
      ...item,
      expanded: true,
      subitems: item.subitems.map((subitem) => this.addExpandedStateToItem(subitem)),
    }
  }

  private flattenItems(items: TemplateItemWithExpandedState[]): FlattenedTemplateItem[] {
    const topLevelItems = items.filter((item) => item.parentId === null).sort((a, b) => a.index - b.index)
    return this.flattenItemsRecursive(topLevelItems, 0, '')
  }

  private flattenItemsRecursive(items: TemplateItemWithExpandedState[], level: number, parentNumber: string): FlattenedTemplateItem[] {
    return items.flatMap((item, index) => {
      const itemNumber = parentNumber ? `${parentNumber}.${index + 1}` : `${index + 1}`
      const flattenedItem: FlattenedTemplateItem = {
        ...item,
        indentLevel: level,
        itemNumber,
      }

      const subitems = item.subitems ?? []
      if (subitems.length > 0) {
        const sortedSubitems = [...subitems].sort((a, b) => a.index - b.index)
        return [flattenedItem, ...this.flattenItemsRecursive(sortedSubitems, level + 1, itemNumber)]
      }

      return [flattenedItem]
    })
  }

  private calculateTotalDuration(items: FlattenedTemplateItem[]): number {
    return items.reduce((sum, item) => sum + (item.duration || 0), 0)
  }
}
